import type { Action } from '@0xtorch/core'
import { and, eq, sql } from 'drizzle-orm'
import { actionSourceCardanoSchema } from '../../querySchema'
import {
  accountTable,
  actionSourceTable,
  actionTable,
  cardanoEventIndexTable,
  lockedPeriodActionTable,
  portfolioTable,
} from '../../schema'
import type { ActionSourceCardano, DatabaseWithTransaction } from '../../types'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createAssetInsertQueries,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  filterActionAccountRelations,
  getClosedAccountingPeriodBoundary,
  prepareFiscalLockInsertRows,
} from './fiscalLock'

/**
 * 成功した Cardano Source のみを Action と解析済み索引に原子的に保存する。
 * 解析済み Source は再生成せず、完全再同期では索引の reset により再解析を許可する。
 * @param params DB、Tx/報酬 Source、既存形式に変換・価格付け済みの Action
 * @returns 保存または既存編集を保護する skip の完了を表す Promise
 */
export const saveCardanoActions = async (params: {
  database: DatabaseWithTransaction
  source: ActionSourceCardano
  actions: readonly Action[]
}): Promise<void> => {
  const { database, transaction } = params.database
  const source = actionSourceCardanoSchema.parse(params.source)
  const indexes = await database
    .select()
    .from(cardanoEventIndexTable)
    .where(
      and(
        eq(cardanoEventIndexTable.sourceId, source.id),
        eq(cardanoEventIndexTable.analyzed, false),
      ),
    )
  if (indexes.length === 0) return
  const timestamp = indexes[0].timestamp.getTime()
  if (indexes.some((index) => index.timestamp.getTime() !== timestamp)) {
    throw new Error('Cardano source timestamps do not match')
  }
  const orders = new Set<number>()
  for (const action of params.actions) {
    if (
      action.source !== source.id ||
      action.timestamp !== timestamp ||
      !Number.isSafeInteger(action.order) ||
      action.order < 0 ||
      orders.has(action.order)
    ) {
      throw new Error('Cardano action does not match its indexed source')
    }
    orders.add(action.order)
  }
  const accounts = await database
    .select({ id: cardanoEventIndexTable.accountId })
    .from(cardanoEventIndexTable)
    .where(eq(cardanoEventIndexTable.sourceId, source.id))
  const [manuallyLocked] = await database
    .select({ id: actionTable.id })
    .from(actionTable)
    .where(
      and(eq(actionTable.sourceId, source.id), eq(actionTable.locked, true)),
    )
    .limit(1)
  const boundary = await getClosedAccountingPeriodBoundary({
    database: params.database,
  })
  const isClosed = boundary !== undefined && timestamp <= boundary.endTimestamp
  const actions = manuallyLocked === undefined ? params.actions : []
  const relations = actions.flatMap((action) =>
    accounts.map((account) => ({
      actionId: `${action.source}_${action.order}`,
      accountId: account.id,
    })),
  )
  const prepared = await prepareFiscalLockInsertRows({
    database: params.database,
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      filterActionAccountRelations({
        actionAccountRelations: relations,
        actionIds: lockedActions.map(
          (action) => `${action.source}_${action.order}`,
        ),
      }),
  })
  await transaction([
    (tx) => [
      (async () => {
        if (manuallyLocked === undefined) {
          await tx
            .insert(actionSourceTable)
            .values(source)
            .onConflictDoUpdate({ target: actionSourceTable.id, set: source })
          if (!isClosed) {
            await deleteAssistantProposalsForSourceIds({
              database: tx,
              sourceIds: [source.id],
            })
            await tx
              .delete(actionTable)
              .where(eq(actionTable.sourceId, source.id))
            await tx.update(portfolioTable).set({ isTxGenerated: false })
          } else {
            await tx
              .delete(lockedPeriodActionTable)
              .where(eq(lockedPeriodActionTable.sourceId, source.id))
          }
        }
      })(),
    ],
    (tx) =>
      createAssetInsertQueries({
        tx,
        openInsertRows: prepared.openInsertRows,
        lockedInsertRows: prepared.lockedInsertRows,
      }),
    (tx) => [
      (async () => {
        if (manuallyLocked === undefined) {
          const lockedInsertRows =
            await applyRulesToLockedInsertRowsInTransaction({
              database: tx,
              lockedInsertRows: prepared.lockedInsertRows,
            })
          await Promise.all([
            ...createOpenActionInsertQueries({
              tx,
              openInsertRows: prepared.openInsertRows,
              actionAccountRelationRows: filterActionAccountRelations({
                actionAccountRelations: relations,
                actionIds: prepared.openActionIds,
              }),
            }),
            ...createLockedActionInsertQueries({ tx, lockedInsertRows }),
          ])
          if (prepared.openActionIds.length > 0) {
            await applyActionEditRulesToInsertedActionsOnDatabase({
              database: tx,
              actionIdList: prepared.openActionIds,
            })
          }
        }
        await tx
          .update(cardanoEventIndexTable)
          .set({ analyzed: true })
          .where(eq(cardanoEventIndexTable.sourceId, source.id))
        for (const account of accounts) {
          await tx
            .update(accountTable)
            .set({
              syncing: sql`(coalesce(${accountTable.cardanoIndexComplete}, 0) = 0 OR EXISTS (SELECT 1 FROM ${cardanoEventIndexTable} WHERE ${cardanoEventIndexTable.accountId} = ${account.id} AND ${cardanoEventIndexTable.analyzed} = 0))`,
            })
            .where(eq(accountTable.id, account.id))
        }
      })(),
    ],
  ])
}
